import { ArrowRight, Link, Link2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';

import { trackEvent } from '../analytics/posthog';
import { isLocalTarget, normalizeTargetUrl } from '../browser/target-url';

/** The line under the headline on every screen that offers the link field. */
export const FEEDBACK_PITCH =
  'Paste a link. Floto captures your screens and asks a panel of personas what works, what doesn’t, and what to fix.';

/**
 * The link field: the one action on the empty home page and on sign-in.
 * Validates and normalises what was typed, then hands the URL up — the home
 * page opens a session with it, sign-in remembers it for after.
 */
export function LinkField({
  onStart,
  variant = 'default',
}: {
  onStart: (url: string) => void;
  /** `hero` is the start screens': a pill with "Create room", greyed until
   *  there is something to capture. */
  variant?: 'default' | 'hero';
}) {
  const hero = variant === 'hero';
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const normalized = normalizeTargetUrl(value);
    if (!normalized) {
      trackEvent('url_rejected', {
        reason: value.trim() ? 'invalid' : 'empty',
      });
      setError(
        value.trim()
          ? "That doesn't look like a link we can open."
          : 'Paste a link to get started.',
      );
      return;
    }
    // Floto opens the page from the internet, so a page only on this
    // machine never loads — say so now, not after a browser has booted.
    if (isLocalTarget(normalized)) {
      trackEvent('url_rejected', { reason: 'localhost' });
      setError(
        'Floto opens pages from the internet, so it can’t reach localhost. Paste a live, staging or preview link instead.',
      );
      return;
    }
    onStart(normalized);
  };

  return (
    <form
      className="w-full duration-700 animate-in fade-in slide-in-from-bottom-4 motion-reduce:animate-none"
      onSubmit={submit}
    >
      <div
        className={
          hero
            ? `flex h-[68px] items-center gap-[8px] rounded-pill border-2 bg-surface pr-2.5 pl-[22px] max-md:pl-4 shadow-url-box transition ${
                error
                  ? 'border-critical'
                  : 'border-url-ring focus-within:border-room-violet'
              }`
            : `flex h-16 items-center gap-3 rounded-panel bg-surface pr-2 pl-5 shadow-floating ring-1 transition ${
                error
                  ? 'ring-critical'
                  : 'ring-hairline focus-within:ring-2 focus-within:ring-ink'
              }`
        }
      >
        {hero ? (
          <Link
            aria-hidden="true"
            className="size-[20px] shrink-0 text-text-secondary"
          />
        ) : (
          <Link2
            aria-hidden="true"
            className="size-5 shrink-0 text-text-muted"
          />
        )}
        <input
          aria-describedby={error ? 'start-error' : undefined}
          aria-invalid={error ? true : undefined}
          aria-label="Product URL"
          className={`h-full min-w-0 flex-1 bg-transparent text-left text-text-primary outline-none placeholder:text-text-muted ${hero ? 'text-heading-xs' : 'text-ui-lg'}`}
          inputMode="url"
          onChange={(event) => {
            setValue(event.target.value);
            setError(null);
          }}
          placeholder={
            hero ? 'https://yourproduct.com/page' : 'yourproduct.com'
          }
          spellCheck={false}
          value={value}
        />
        <button
          className={
            hero
              ? `flex h-[50px] shrink-0 cursor-pointer items-center gap-[8px] rounded-pill pr-[22px] pl-6 max-md:px-4 text-ui-lg font-bold text-white transition active:scale-[0.98] ${
                  value.trim()
                    ? 'bg-brand hover:bg-brand-alt'
                    : 'bg-control-idle'
                }`
              : 'flex h-12 shrink-0 cursor-pointer items-center gap-2 rounded-button bg-ink px-5 text-ui-strong font-semibold text-white transition hover:bg-ink/90 active:scale-[0.98]'
          }
          type="submit"
        >
          {/* On a phone the arrow alone, so the URL has the room. */}
          <span className={hero ? 'max-md:sr-only' : undefined}>
            {hero ? 'Create room' : 'Get feedback'}
          </span>
          <ArrowRight aria-hidden="true" className="size-4" />
        </button>
      </div>

      {error ? (
        <p
          className="pt-3 text-ui text-critical duration-200 animate-in fade-in"
          id="start-error"
          role="alert"
        >
          {error}
        </p>
      ) : null}
    </form>
  );
}
